import { Modal, ModalProps, Tabs } from 'antd';
import AudioPlayer from 'component/common/AudioPlayer/audioPlayer';
import { useEffect, useState } from 'react';
import { Lecture, Theory } from '../../../model/response';
import RenderContent from '../RenderContent';
import {
  AudioOutlined,
  FileMarkdownOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { findTheoryById } from '../../../service/theoryService';
import processError from '../../../utils/processError';
import LoadingArea from '../LoadingArea';
import { findLectureById } from 'service/lectureService';

interface LectureTheoryModalProps extends Omit<ModalProps, 'footer' | 'width' | 'destroyOnClose'> {
  type?: 'LECTURE' | 'THEORY';
  id?: string | null;
}

const LectureTheoryModal = ({ id, type = 'LECTURE', open, ...restProps }: LectureTheoryModalProps) => {
  const [data, setData] = useState<Lecture | Theory | null>(null);
  const [activeTabIndex, setActiveTabIndex] = useState('0');
  const [fetching, setFetching] = useState(false);

  const getIconForFormat = (format: string, fileType: string) => {
    if (format === 'TEXT') {
      switch (fileType) {
        case 'doc':
        case 'docx':
          return <FileWordOutlined style={{ color: '#4285F4' }} />; // Icon cho Word
        case 'pdf':
          return <FilePdfOutlined style={{ color: '#FF0000' }} />; // Icon cho PDF
        case 'ppt':
        case 'pptx':
          return <FilePptOutlined style={{ color: '#D04423' }} />; // Icon cho PowerPoint
        case 'html':
          return <FileMarkdownOutlined style={{ color: '#E34C26' }} />; // Icon cho HTML
        default:
          return <FileTextOutlined style={{ color: '#1890ff' }} />; // Icon mặc định cho văn bản
      }
    } else if (format === 'VIDEO') {
      return <VideoCameraOutlined style={{ color: '#faad14' }} />; // Icon cho video
    } else if (format === 'AUDIO') {
      return <AudioOutlined style={{ color: '#52c41a' }} />; // Icon cho audio
    }
    return null; // Trường hợp không có định dạng phù hợp
  };  

  const handleActiveTab = (key:string) => {
    setActiveTabIndex(key)
  }

  useEffect(() => {
    if (!id) {
      return;
    }
    if (open) {
      setFetching(true);
      if (type === 'LECTURE') {
        findLectureById(id)
          .then(setData)
          .catch(processError)
          .finally(() => setFetching(false));
      } else {
        findTheoryById(id)
          .then(setData)
          .catch(processError)
          .finally(() => setFetching(false));
      }
    }
  }, [id, open, type]);

  return (
    <Modal open={open} footer={null} width={950} destroyOnClose={true} {...restProps}>
      {fetching ? (
        <LoadingArea />
      ) : (
        <div className="p-3">
          <div className="mb-3 text-primary flex flex-wrap items-center justify-between">
            <h3 className="text-base font-medium">{data?.name}</h3>
            {data?.contents && data?.contents[parseInt(activeTabIndex)]?.audioPath && (
              <AudioPlayer url={data.contents[parseInt(activeTabIndex)].audioPath} />
            )}
          </div>
          {data?.contents && (
            <Tabs
              defaultActiveKey={'0'}
              activeKey={activeTabIndex}
              onChange={(key) => handleActiveTab(key)}
              items={data.contents.map((content, index) => ({
                key: index.toString(),
                label: <span>{getIconForFormat(content?.format, content?.fileType)}</span>,
                children: <RenderContent content={content} />,
              }))}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default LectureTheoryModal;
